// ReportingEngineHelp.tsx
import React, { useMemo, useRef, useState } from "react";
import {
  Drawer,
  IconButton,
  TextField,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import BarChartIcon from "@mui/icons-material/BarChart";
import PieChartIcon from "@mui/icons-material/PieChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import SettingsIcon from "@mui/icons-material/Settings";
import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import PublishIcon from "@mui/icons-material/Publish";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

type PDFLink = { title: string; href: string };
type StepBlock = { title: string; bullets: string[] };
type ChartSetting = { setting: string; desc: string };

const DEFAULT_PDFS = [
  {
    title: "Reporting Engine User Manual",
    href: "https://mdimages-test.s3.amazonaws.com/3a4e4e9a-dd84-4aba-a82a-6ee6aaf57b88_Reporting_Engine_User_Manual.pdf",
  },
];

// ---- Static Content ----

const CHART_TYPES = [
  { name: "Bar Chart", icon: <BarChartIcon />, desc: "Compare values across categories." },
  { name: "Line Chart", icon: <ShowChartIcon />, desc: "Show trends over time or continuous data." },
  { name: "Pie Chart", icon: <PieChartIcon />, desc: "Show proportions and percentages." },
];

const CHART_SETTINGS: ChartSetting[] = [
  { setting: "Name", desc: "The display name for this specific chart within the report." },
  { setting: "Filters", desc: "Conditions applied to the Form data to narrow down which records are included." },
  { setting: "X-Axis Columns", desc: "Fields from the Form plotted along the X-axis — categories, dates, or labels." },
  { setting: "Y-Axis Columns", desc: "Fields from the Form plotted along the Y-axis — counts, sums, or measured values." },
  { setting: "Sorting", desc: "Defines the order in which data points are arranged (ascending, descending, by date)." },
];

const CREATE_REPORT_STEPS: StepBlock[] = [
  { title: "Open Reporting", bullets: ["Click Reporting in the left sidebar."] },
  { title: "Select Business Service and Form", bullets: ["Choose the Business Service and Form whose data you want to report on."] },
  { title: "Click Create Report Format", bullets: ["Start building a new report."] },
  { title: "Enter Report Name and Description", bullets: ["Type a clear, descriptive name and description, then click Continue."] },
  { title: "Choose a Chart Type", bullets: ["Drag the type of chart you want onto the canvas."] },
  { title: "Open Settings", bullets: ["Click Settings on the chart to configure how it displays data."] },
  { title: "Configure Chart", bullets: ["Set Name, Filters, X-Axis, Y-Axis, and Sorting."] },
  { title: "Save", bullets: ["Click Save within the Settings panel to save your chart configuration."] },
  { title: "Save Report Format", bullets: ["Click the Save button at the top right to save the overall report."] },
  { title: "Publish", bullets: ["Click Update to publish the report, making it available to users."] },
];

const SectionHeader: React.FC<{ title: string; subtitle?: string; id?: string }> = ({ title, subtitle, id }) => (
  <Box id={id} sx={{ mb: 1.25, mt: 3 }}>
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

const StepAccordion: React.FC<{ steps: StepBlock[]; label: string; defaultExpanded?: boolean }> = ({
  steps,
  label,
  defaultExpanded,
}) => {
  const [active, setActive] = useState(0);
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ mb: 2, borderRadius: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>
          {label} ({steps.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stepper activeStep={active} alternativeLabel sx={{ mb: 2, flexWrap: "wrap" }}>
          {steps.map((s, i) => (
            <Step key={s.title} onClick={() => setActive(i)}>
              <StepLabel>{s.title}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {steps.map((s, idx) => {
          const isActive = idx === active;
          return (
            <Box
              key={s.title}
              sx={{
                mb: 1.25,
                p: 1.25,
                border: (t) => `1px dashed ${t.palette.divider}`,
                borderRadius: 2,
                bgcolor: (t) => (isActive ? t.palette.action.hover : "transparent"),
              }}
            >
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {idx + 1}. {s.title}
              </Typography>
              <List dense sx={{ pt: 0 }}>
                {s.bullets.map((b, j) => (
                  <ListItem key={j} sx={{ py: 0.2 }}>
                    <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={`• ${b}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        })}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setActive((p) => Math.max(0, p - 1))}>
            Previous
          </Button>
          <Button variant="contained" onClick={() => setActive((p) => Math.min(steps.length - 1, p + 1))}>
            Next
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const ReportingEngineHelp: React.FC<{ pdfs?: PDFLink[] }> = ({ pdfs }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const jumpTo = (hash: string) => {
    const el = contentRef.current?.querySelector(hash);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setMenuEl(e.currentTarget);
  const handleCloseMenu = () => setMenuEl(null);
  const handlePrint = () => {
    window.open(DEFAULT_PDFS[0].href, "_blank");
  };

  const visiblePdfs = pdfs && pdfs.length > 0 ? pdfs : DEFAULT_PDFS;

  const sectionTexts = useMemo(() => {
    const flatten = (steps: StepBlock[]) => steps.map((s) => `${s.title} ${s.bullets.join(" ")}`).join(" ");
    
    return {
      overview: "reporting engine no-code low-code dynamic charts business service form",
      chartTypes: "bar chart line chart pie chart data visualization",
      settings: "name filters x-axis y-axis sorting configuration",
      createReport: flatten(CREATE_REPORT_STEPS) + " report name description chart type publish",
      draftVsPublished: "draft save unpublished published update visible report list",
    };
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (sectionKey: keyof typeof sectionTexts) =>
    !q || sectionTexts[sectionKey].toLowerCase().includes(q);

  return (
    <div>
      <Tooltip title="Reporting Engine Help">
        <IconButton onClick={() => setIsOpen(true)}>
          <HelpOutlineIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)} sx={{ zIndex: 1400 }}>
        <Box
          sx={{
            width: 560,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: (t) => (t.palette.mode === "light" ? "#fff" : "background.paper"),
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "linear-gradient(90deg, rgba(99,102,241,0.16), rgba(14,165,233,0.16))",
              backdropFilter: "saturate(1.2)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <BarChartIcon />
              <Typography variant="h6" fontWeight={800}>
                Reporting Engine • Help
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Menu
                anchorEl={menuEl}
                open={Boolean(menuEl)}
                onClose={handleCloseMenu}
              >
                {visiblePdfs.map((p) => (
                  <MenuItem
                    key={p.title}
                    component={p.href ? "a" : "li"}
                    href={p.href || undefined}
                    target={p.href ? "_blank" : undefined}
                    rel={p.href ? "noopener noreferrer" : undefined}
                    onClick={handleCloseMenu}
                  >
                    <PictureAsPdfIcon fontSize="small" style={{ marginRight: 8 }} />
                    {p.title}
                  </MenuItem>
                ))}
              </Menu>
              <Tooltip title="Print / Save as PDF">
                <IconButton onClick={handlePrint}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Search + Quick Jump */}
          <Box sx={{ px: 2, pt: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search charts, filters, settings, publish, draft…"
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              <Chip label="Overview" size="small" variant="outlined" onClick={() => jumpTo("#sec-overview")} sx={{ mb: 1 }} />
              <Chip label="Chart Types" size="small" variant="outlined" onClick={() => jumpTo("#sec-chart-types")} sx={{ mb: 1 }} />
              <Chip label="Settings" size="small" variant="outlined" onClick={() => jumpTo("#sec-settings")} sx={{ mb: 1 }} />
              <Chip label="Create Report" size="small" variant="outlined" onClick={() => jumpTo("#sec-create-report")} sx={{ mb: 1 }} />
              <Chip label="Draft vs Published" size="small" variant="outlined" onClick={() => jumpTo("#sec-draft-published")} sx={{ mb: 1 }} />
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Scrollable Content */}
          <Box ref={contentRef} sx={{ px: 2, pb: 3, overflowY: "auto" }}>
            {/* Overview */}
            {matches("overview") && (
              <>
                <SectionHeader id="sec-overview" title="Overview" subtitle="Build dynamic, visual reports without writing queries." />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    The Reporting Engine is a no-code / low-code tool that lets you build dynamic, visual reports directly from data captured in your Business Services.
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Card variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography variant="body2" fontWeight={600}>No-code</Typography>
                        <Typography variant="body2">Drag-and-drop interface</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={4}>
                      <Card variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography variant="body2" fontWeight={600}>Dynamic Charts</Typography>
                        <Typography variant="body2">Multiple chart types</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={4}>
                      <Card variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography variant="body2" fontWeight={600}>Publishing</Typography>
                        <Typography variant="body2">Save drafts, publish reports</Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Card>
              </>
            )}

            {/* Chart Types */}
            {matches("chartTypes") && (
              <>
                <SectionHeader id="sec-chart-types" title="Chart Types" />
                <Grid container spacing={1}>
                  {CHART_TYPES.map((chart) => (
                    <Grid item xs={4} key={chart.name}>
                      <Card variant="outlined" sx={{ borderRadius: 2, textAlign: "center", p: 1.5 }}>
                        {chart.icon}
                        <Typography fontWeight={600} sx={{ mt: 0.5 }}>{chart.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{chart.desc}</Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {/* Chart Settings */}
            {matches("settings") && (
              <>
                <SectionHeader id="sec-settings" title="Chart Settings Reference" />
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Setting</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {CHART_SETTINGS.map((row) => (
                        <TableRow key={row.setting}>
                          <TableCell>{row.setting}</TableCell>
                          <TableCell>{row.desc}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Create Report */}
            {matches("createReport") && (
              <>
                <SectionHeader id="sec-create-report" title="Creating a New Report" />
                <StepAccordion steps={CREATE_REPORT_STEPS} label="Creation Steps" defaultExpanded />
              </>
            )}

            {/* Draft vs Published */}
            {matches("draftVsPublished") && (
              <>
                <SectionHeader id="sec-draft-published" title="Draft vs. Published" />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Saving a report stores your work, but the report only becomes visible to users after it has been published.
                  </Typography>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    <strong>Draft:</strong> Saved but not visible to users. Return to continue editing.
                  </Alert>
                  <Alert severity="success">
                    <strong>Published:</strong> Available in the Report List to relevant users via Update.
                  </Alert>
                </Card>
                <Alert severity="info">
                  Published reports can be triggered to send automatically via email. Contact platform support for detailed configuration steps.
                </Alert>
              </>
            )}

            {/* Footer */}
            <Box sx={{ mt: 2, px: 1.5, py: 1, borderTop: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Tip: Use the Download icon in the header to fetch the full manual PDF, or print this page to PDF.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
};

export default ReportingEngineHelp;